/**
 * 评估器的查找表与位运算原语。见 docs/ARCHITECTURE.md §4.2。
 *
 * 这些表在模块加载期一次性构建（8KB），之后整个进程只读。放在独立模块里是为了让
 * 「表怎么来的」和「牌怎么评」互不干扰：顺子判定是评估器里最容易写错的一环
 * （见下面轮子的全等匹配注释），单独成文才好为它写针对性的测试。
 *
 * 约定：点数位图（rankMask）是 13 位整数，第 r 位为 1 表示存在点数 r 的牌，
 * r 的编码与 cards.ts 一致（0 = 2，12 = A）。
 */

/** 点数位图的位数，与 NUM_RANKS 相同；这里重复一个字面量是为了让表的尺寸在本文件内自洽 */
const RANK_BITS = 13

/** A,5,4,3,2 —— 轮子的完整点数集合 */
const WHEEL_MASK = (1 << 12) | (1 << 3) | (1 << 2) | (1 << 1) | (1 << 0)

/**
 * `STRAIGHT_TABLE[rankMask13] = highRank + 1`，0 表示无顺子。
 *
 * 存 `highRank + 1` 而不是 `highRank` 是为了让「无顺子」用 0 表示，
 * 从而整张表能塞进 Uint8Array（highRank 最大 12，+1 后 13 仍 < 256）。
 */
function buildStraightTable(): Uint8Array {
  const t = new Uint8Array(1 << RANK_BITS)
  for (let mask = 0; mask < 1 << RANK_BITS; mask++) {
    let v = 0
    // 从高往低找第一个 5 连，找到即最大顺子
    for (let high = 12; high >= 4; high--) {
      const pat = 0b11111 << (high - 4)
      if ((mask & pat) === pat) {
        v = high + 1
        break
      }
    }
    // 轮子必须是**全等**匹配：写成「有 A 且有 2345 中任意一张」会把 A-2-3-4-x
    // 误判成顺子——这是原型实现里真实发生过的 bug。
    if (v === 0 && (mask & WHEEL_MASK) === WHEEL_MASK) v = 3 + 1
    t[mask] = v
  }
  return t
}

export const STRAIGHT_TABLE: Uint8Array = buildStraightTable()

/**
 * 返回顺子的最大点数，无顺子返回 -1。
 * 轮子 A-2-3-4-5 返回 3（即「5 高」），A 在轮子里只当 1。
 */
export function straightHigh(rankMask: number): number {
  return STRAIGHT_TABLE[rankMask]! - 1
}

/** A,9,8,7,6 —— 短牌最小的顺子 */
const SHORT_WHEEL_MASK = (1 << 12) | (1 << 7) | (1 << 6) | (1 << 5) | (1 << 4)

/**
 * 短牌的顺子：A-6-7-8-9 算顺子，记作「9 高」（返回 7），排在 6-7-8-9-10 之下；
 * 没有 A-2-3-4-5（2-5 不在牌堆里）。同样要求**全等**匹配。
 */
export function straightHighShort(rankMask: number): number {
  const m = rankMask & ~0b1111 // 2-5 不存在；万一混进来也不让它们凑出标准轮子
  const std = STRAIGHT_TABLE[m]! - 1
  if (std >= 0) return std
  return (m & SHORT_WHEEL_MASK) === SHORT_WHEEL_MASK ? 7 : -1
}

/** 最高置位的位序；mask 为 0 时返回 -1（clz32(0) === 32） */
export function highBit(mask: number): number {
  return 31 - Math.clz32(mask)
}

/** SWAR popcount，避免在热路径上循环移位 */
export function popcount(mask: number): number {
  let x = mask - ((mask >> 1) & 0x5555_5555)
  x = (x & 0x3333_3333) + ((x >> 2) & 0x3333_3333)
  x = (x + (x >> 4)) & 0x0f0f_0f0f
  return Math.imul(x, 0x0101_0101) >> 24
}

/** 从高到低取 n 个置位的位序；置位不足时返回的数组更短（调用方自负） */
export function topN(mask: number, n: number): number[] {
  const out: number[] = []
  let m = mask
  while (out.length < n && m !== 0) {
    const b = highBit(m)
    out.push(b)
    m &= ~(1 << b)
  }
  return out
}
